// apps/web/src/pages/AdminPage.tsx
//
// Platform admin panel for GeekyBee internal staff. Provides cross-tenant
// visibility into all users, artists, and organisations on the platform.

import { useEffect, useState } from 'react'
import {
  Shield,
  Users,
  Music,
  Building2,
  Search,
  ExternalLink,
  CheckCircle,
  XCircle,
  Activity,
} from 'lucide-react'
import { apiFetch, ApiClientError } from '@/lib/api-client'
import { useAuthStore } from '@/lib/auth-store'
import { Logo } from '@/components/Logo'

// ── Types ──────────────────────────────────────────────────────────────────────

interface PlatformStats {
  userCount: number
  artistCount: number
  orgCount: number
  showCount: number
}

interface AdminUser {
  id: string
  email: string
  displayName: string
  accountType: string
  emailVerified: boolean
  createdAt: string
}

interface AdminUserDetail extends AdminUser {
  updatedAt: string
  artistMemberships: Array<{
    artistId: string
    role: string
    artist: { name: string; slug: string }
  }>
  orgMemberships: Array<{
    organisationId: string
    role: string
    organisation: { name: string; slug: string }
  }>
}

interface AdminArtist {
  id: string
  name: string
  slug: string
  createdAt: string
  organisation: { id: string; name: string } | null
  showCount: number
  memberCount: number
}

interface AdminOrg {
  id: string
  name: string
  slug: string
  plan: string | null
  createdAt: string
  artistCount: number
  userCount: number
}

interface PaginatedResponse<T> {
  data: T[]
  meta: { page: number; limit: number; total: number }
}

// ── Tabs ───────────────────────────────────────────────────────────────────────

type Tab = 'dashboard' | 'users' | 'artists' | 'organisations' | 'audience-health'

interface AudienceHealthRow {
  id: string
  platform: string
  displayLabel: string | null
  connectedAt: string
  lastUsedAt: string | null
  lastErrorAt: string | null
  lastErrorMessage: string | null
  artistId: string
  artistName: string
  artistSlug: string
  organisationId: string | null
  organisationName: string | null
}

export function AdminPage() {
  const [activeTab, setActiveTab] = useState<Tab>('dashboard')

  const tabs: { key: Tab; label: string; icon: typeof Shield }[] = [
    { key: 'dashboard', label: 'Dashboard', icon: Shield },
    { key: 'users', label: 'Users', icon: Users },
    { key: 'artists', label: 'Artists', icon: Music },
    { key: 'organisations', label: 'Organisations', icon: Building2 },
    { key: 'audience-health', label: 'Audience Health', icon: Activity },
  ]

  return (
    <div>
      <div className="mb-6">
        <Logo showWordmark={false} className="mb-2" />
        <h1 className="text-xl font-bold text-ovation-ink sm:text-2xl">
          Platform Admin
        </h1>
        <p className="mt-1 text-sm text-ovation-muted">
          GeekyBee internal management panel
        </p>
      </div>

      <div className="mb-6 flex gap-1 overflow-x-auto border-b border-ovation-ink/10">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            type="button"
            onClick={() => setActiveTab(tab.key)}
            className={`flex shrink-0 items-center gap-2 border-b-2 px-3 py-2 text-sm font-medium transition-colors sm:px-4 ${
              activeTab === tab.key
                ? 'border-ovation-ink text-ovation-ink'
                : 'border-transparent text-ovation-muted hover:text-ovation-ink'
            }`}
          >
            <tab.icon className="h-4 w-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'dashboard' && <DashboardTab />}
      {activeTab === 'users' && <UsersTab />}
      {activeTab === 'artists' && <ArtistsTab />}
      {activeTab === 'organisations' && <OrganisationsTab />}
      {activeTab === 'audience-health' && <AudienceHealthTab />}
    </div>
  )
}

// ── Audience Health Tab ────────────────────────────────────────────────────────

function AudienceHealthTab() {
  const [rows, setRows] = useState<AudienceHealthRow[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [platformFilter, setPlatformFilter] = useState<string>('')
  const [errorsOnly, setErrorsOnly] = useState(false)

  useEffect(() => {
    async function load() {
      setLoading(true)
      setError(null)
      try {
        const qs = new URLSearchParams()
        if (platformFilter) qs.set('platform', platformFilter)
        if (errorsOnly) qs.set('errorsOnly', 'true')
        const data = await apiFetch<AudienceHealthRow[]>(
          `/admin/audience-health?${qs.toString()}`,
        )
        setRows(Array.isArray(data) ? data : [])
      } catch (err) {
        console.error('Failed to load audience health', err)
        setRows([])
        setError(
          err instanceof ApiClientError
            ? err.message
            : 'Failed to load audience integrations.',
        )
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [platformFilter, errorsOnly])

  return (
    <div className="space-y-4">
      <p className="text-sm text-ovation-muted">
        Cross-tenant view of every connected audience platform (Spotify, Meta,
        GA4, Chartmetric, etc.) — when each credential last synced, and which
        ones are reporting errors. Use this to diagnose &ldquo;their Instagram
        integration isn&apos;t working&rdquo; support calls.
      </p>

      {error && (
        <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}
      <div className="flex flex-wrap items-center gap-3">
        <div>
          <label className="mr-2 text-sm text-ovation-muted">Platform</label>
          <select
            value={platformFilter}
            onChange={(e) => setPlatformFilter(e.target.value)}
            className="rounded border border-ovation-ink/20 bg-white px-2 py-1 text-sm"
          >
            <option value="">All</option>
            <option value="spotify">Spotify</option>
            <option value="meta">Facebook</option>
            <option value="instagram">Instagram</option>
            <option value="youtube">YouTube</option>
            <option value="ga4">GA4</option>
            <option value="search_console">Search Console</option>
            <option value="chartmetric">Chartmetric</option>
            <option value="hivecells">HiveCells</option>
          </select>
        </div>
        <label className="inline-flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={errorsOnly}
            onChange={(e) => setErrorsOnly(e.target.checked)}
            className="h-4 w-4"
          />
          Errors only
        </label>
        <span className="ml-auto text-xs text-ovation-muted">
          {loading ? 'Loading…' : `${rows.length} credential${rows.length === 1 ? '' : 's'}`}
        </span>
      </div>

      <div className="overflow-hidden rounded-lg border border-ovation-ink/10">
        {loading ? (
          <LoadingSpinner />
        ) : (
        <table className="w-full text-sm">
          <thead className="bg-ovation-ink/5">
            <tr>
              <th className="px-3 py-2 text-left font-medium text-ovation-muted">Artist</th>
              <th className="px-3 py-2 text-left font-medium text-ovation-muted">Org</th>
              <th className="px-3 py-2 text-left font-medium text-ovation-muted">Platform</th>
              <th className="px-3 py-2 text-left font-medium text-ovation-muted">Connected</th>
              <th className="px-3 py-2 text-left font-medium text-ovation-muted">Last sync</th>
              <th className="px-3 py-2 text-left font-medium text-ovation-muted">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-ovation-ink/5">
            {rows.length === 0 && !loading && (
              <tr>
                <td colSpan={6} className="px-3 py-6 text-center text-ovation-muted">
                  No credentials match these filters.
                </td>
              </tr>
            )}
            {rows.map((r) => (
              <tr key={r.id}>
                <td className="px-3 py-2 text-ovation-ink">{r.artistName}</td>
                <td className="px-3 py-2 text-ovation-muted">{r.organisationName ?? '—'}</td>
                <td className="px-3 py-2">
                  <span className="rounded bg-ovation-ink/5 px-1.5 py-0.5 font-mono text-xs">{r.platform}</span>
                  {r.displayLabel && (
                    <span className="ml-2 text-xs text-ovation-muted">{r.displayLabel}</span>
                  )}
                </td>
                <td className="px-3 py-2 text-ovation-muted">{new Date(r.connectedAt).toLocaleDateString()}</td>
                <td className="px-3 py-2 text-ovation-muted">
                  {r.lastUsedAt ? new Date(r.lastUsedAt).toLocaleString() : '—'}
                </td>
                <td className="px-3 py-2">
                  {r.lastErrorMessage ? (
                    <span className="inline-flex items-start gap-1 text-red-700" title={r.lastErrorMessage}>
                      <XCircle className="h-4 w-4 shrink-0" />
                      <span className="line-clamp-1 max-w-md text-xs">
                        {r.lastErrorMessage.slice(0, 80)}
                        {r.lastErrorMessage.length > 80 && '…'}
                      </span>
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-emerald-700">
                      <CheckCircle className="h-4 w-4" />
                      <span className="text-xs">OK</span>
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        )}
      </div>
    </div>
  )
}

// ── Dashboard Tab ──────────────────────────────────────────────────────────────

function DashboardTab() {
  const [stats, setStats] = useState<PlatformStats | null>(null)
  const [recentUsers, setRecentUsers] = useState<AdminUser[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    async function load() {
      try {
        const [statsData, usersData] = await Promise.all([
          apiFetch<PlatformStats>('/admin/stats'),
          apiFetch<AdminUser[]>('/admin/users?limit=10'),
        ])
        setStats(statsData)
        setRecentUsers(usersData)
      } catch (err) {
        console.error('Failed to load admin stats', err)
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [])

  if (loading) return <LoadingSpinner />

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard label="Total Users" value={stats?.userCount ?? 0} icon={Users} />
        <StatCard label="Total Artists" value={stats?.artistCount ?? 0} icon={Music} />
        <StatCard label="Total Organisations" value={stats?.orgCount ?? 0} icon={Building2} />
        <StatCard label="Total Shows" value={stats?.showCount ?? 0} icon={Shield} />
      </div>

      <div>
        <h2 className="mb-3 text-lg font-semibold text-ovation-ink">
          Recent Signups
        </h2>
        <div className="overflow-hidden rounded-lg border border-ovation-ink/10">
          <table className="w-full text-sm">
            <thead className="bg-ovation-ink/5">
              <tr>
                <th className="px-4 py-2 text-left font-medium text-ovation-muted">Email</th>
                <th className="px-4 py-2 text-left font-medium text-ovation-muted">Name</th>
                <th className="px-4 py-2 text-left font-medium text-ovation-muted">Type</th>
                <th className="px-4 py-2 text-left font-medium text-ovation-muted">Joined</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-ovation-ink/5">
              {recentUsers.map((u) => (
                <tr key={u.id}>
                  <td className="px-4 py-2 text-ovation-ink">{u.email}</td>
                  <td className="px-4 py-2 text-ovation-ink">{u.displayName}</td>
                  <td className="px-4 py-2">
                    <AccountTypeBadge type={u.accountType} />
                  </td>
                  <td className="px-4 py-2 text-ovation-muted">
                    {new Date(u.createdAt).toLocaleDateString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

// ── Users Tab ──────────────────────────────────────────────────────────────────

function UsersTab() {
  const [users, setUsers] = useState<AdminUser[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [searchInput, setSearchInput] = useState('')
  const [loading, setLoading] = useState(true)
  const [expandedId, setExpandedId] = useState<string | null>(null)
  const [expandedUser, setExpandedUser] = useState<AdminUserDetail | null>(null)

  const limit = 25

  useEffect(() => {
    async function load() {
      setLoading(true)
      try {
        const params = new URLSearchParams({ page: String(page), limit: String(limit) })
        if (search) params.set('search', search)
        const res = await apiFetch<AdminUser[]>(`/admin/users?${params}`)
        // The response is actually { data, meta } but apiFetch unwraps to data
        // We need to get meta separately - use raw approach
        setUsers(res)
        // Estimate total from response length for now
        setTotal(res.length < limit ? (page - 1) * limit + res.length : page * limit + 1)
      } catch (err) {
        console.error('Failed to load users', err)
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [page, search])

  async function handleExpand(userId: string) {
    if (expandedId === userId) {
      setExpandedId(null)
      setExpandedUser(null)
      return
    }
    setExpandedId(userId)
    try {
      const detail = await apiFetch<AdminUserDetail>(`/admin/users/${userId}`)
      setExpandedUser(detail)
    } catch (err) {
      console.error('Failed to load user detail', err)
    }
  }

  async function handleVerifyEmail(userId: string) {
    try {
      await apiFetch(`/admin/users/${userId}`, {
        method: 'PATCH',
        body: { emailVerified: true },
      })
      setUsers((prev) =>
        prev.map((u) => (u.id === userId ? { ...u, emailVerified: true } : u)),
      )
    } catch (err) {
      console.error('Failed to verify user email', err)
    }
  }

  async function handleImpersonate(userId: string) {
    try {
      const result = await apiFetch<{ accessToken: string; user: any }>(
        `/admin/impersonate/${userId}`,
        { method: 'POST' },
      )
      // Open in new tab with the impersonated token
      const url = new URL(window.location.origin)
      url.searchParams.set('impersonate_token', result.accessToken)
      window.open(url.toString(), '_blank')
    } catch (err) {
      console.error('Failed to impersonate user', err)
    }
  }

  function handleSearch(e: React.FormEvent) {
    e.preventDefault()
    setPage(1)
    setSearch(searchInput)
  }

  return (
    <div className="space-y-4">
      <form onSubmit={handleSearch} className="flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ovation-muted" />
          <input
            type="text"
            placeholder="Search by email or name..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="w-full rounded-md border border-ovation-ink/20 bg-white py-2 pl-10 pr-4 text-sm focus:border-ovation-ink focus:outline-none"
          />
        </div>
        <button
          type="submit"
          className="rounded-md bg-ovation-ink px-4 py-2 text-sm text-ovation-paper hover:bg-ovation-ink/90"
        >
          Search
        </button>
      </form>

      {loading ? (
        <LoadingSpinner />
      ) : (
        <div className="overflow-hidden rounded-lg border border-ovation-ink/10">
          <table className="w-full text-sm">
            <thead className="bg-ovation-ink/5">
              <tr>
                <th className="px-4 py-2 text-left font-medium text-ovation-muted">Email</th>
                <th className="px-4 py-2 text-left font-medium text-ovation-muted">Name</th>
                <th className="px-4 py-2 text-left font-medium text-ovation-muted">Type</th>
                <th className="px-4 py-2 text-left font-medium text-ovation-muted">Verified</th>
                <th className="px-4 py-2 text-left font-medium text-ovation-muted">Created</th>
                <th className="px-4 py-2 text-left font-medium text-ovation-muted">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-ovation-ink/5">
              {users.map((u) => (
                <>
                  <tr
                    key={u.id}
                    className="cursor-pointer hover:bg-ovation-ink/5"
                    onClick={() => handleExpand(u.id)}
                  >
                    <td className="px-4 py-2 text-ovation-ink">{u.email}</td>
                    <td className="px-4 py-2 text-ovation-ink">{u.displayName}</td>
                    <td className="px-4 py-2">
                      <AccountTypeBadge type={u.accountType} />
                    </td>
                    <td className="px-4 py-2">
                      {u.emailVerified ? (
                        <CheckCircle className="h-4 w-4 text-green-600" />
                      ) : (
                        <XCircle className="h-4 w-4 text-red-500" />
                      )}
                    </td>
                    <td className="px-4 py-2 text-ovation-muted">
                      {new Date(u.createdAt).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-2">
                      <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
                        {!u.emailVerified && (
                          <button
                            type="button"
                            onClick={() => handleVerifyEmail(u.id)}
                            className="rounded bg-green-100 px-2 py-1 text-xs text-green-800 hover:bg-green-200"
                          >
                            Verify
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => handleImpersonate(u.id)}
                          className="flex items-center gap-1 rounded bg-amber-100 px-2 py-1 text-xs text-amber-800 hover:bg-amber-200"
                        >
                          <ExternalLink className="h-3 w-3" />
                          Impersonate
                        </button>
                      </div>
                    </td>
                  </tr>
                  {expandedId === u.id && expandedUser && (
                    <tr key={`${u.id}-detail`}>
                      <td colSpan={6} className="bg-ovation-ink/[0.02] px-6 py-4">
                        <div className="space-y-3">
                          {expandedUser.artistMemberships.length > 0 && (
                            <div>
                              <h4 className="text-xs font-semibold uppercase text-ovation-muted">
                                Artist Memberships
                              </h4>
                              <ul className="mt-1 space-y-1">
                                {expandedUser.artistMemberships.map((m) => (
                                  <li key={m.artistId} className="text-sm text-ovation-ink">
                                    {m.artist.name} ({m.artist.slug}) — {m.role}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                          {expandedUser.orgMemberships.length > 0 && (
                            <div>
                              <h4 className="text-xs font-semibold uppercase text-ovation-muted">
                                Organisation Memberships
                              </h4>
                              <ul className="mt-1 space-y-1">
                                {expandedUser.orgMemberships.map((m) => (
                                  <li key={m.organisationId} className="text-sm text-ovation-ink">
                                    {m.organisation.name} ({m.organisation.slug}) — {m.role}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                          {expandedUser.artistMemberships.length === 0 &&
                            expandedUser.orgMemberships.length === 0 && (
                              <p className="text-sm text-ovation-muted">
                                No memberships found.
                              </p>
                            )}
                        </div>
                      </td>
                    </tr>
                  )}
                </>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="flex items-center justify-between">
        <button
          type="button"
          disabled={page === 1}
          onClick={() => setPage((p) => p - 1)}
          className="rounded-md border border-ovation-ink/20 px-3 py-1 text-sm disabled:opacity-50"
        >
          Previous
        </button>
        <span className="text-sm text-ovation-muted">Page {page}</span>
        <button
          type="button"
          disabled={users.length < limit}
          onClick={() => setPage((p) => p + 1)}
          className="rounded-md border border-ovation-ink/20 px-3 py-1 text-sm disabled:opacity-50"
        >
          Next
        </button>
      </div>
    </div>
  )
}

// ── Artists Tab ─────────────────────────────────────────────────────────────────

function ArtistsTab() {
  const [artists, setArtists] = useState<AdminArtist[]>([])
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [searchInput, setSearchInput] = useState('')
  const [loading, setLoading] = useState(true)

  const limit = 25

  useEffect(() => {
    async function load() {
      setLoading(true)
      try {
        const params = new URLSearchParams({ page: String(page), limit: String(limit) })
        if (search) params.set('search', search)
        const res = await apiFetch<AdminArtist[]>(`/admin/artists?${params}`)
        setArtists(res)
      } catch (err) {
        console.error('Failed to load artists', err)
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [page, search])

  function handleSearch(e: React.FormEvent) {
    e.preventDefault()
    setPage(1)
    setSearch(searchInput)
  }

  return (
    <div className="space-y-4">
      <form onSubmit={handleSearch} className="flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ovation-muted" />
          <input
            type="text"
            placeholder="Search artists..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="w-full rounded-md border border-ovation-ink/20 bg-white py-2 pl-10 pr-4 text-sm focus:border-ovation-ink focus:outline-none"
          />
        </div>
        <button
          type="submit"
          className="rounded-md bg-ovation-ink px-4 py-2 text-sm text-ovation-paper hover:bg-ovation-ink/90"
        >
          Search
        </button>
      </form>

      {loading ? (
        <LoadingSpinner />
      ) : (
        <div className="overflow-hidden rounded-lg border border-ovation-ink/10">
          <table className="w-full text-sm">
            <thead className="bg-ovation-ink/5">
              <tr>
                <th className="px-4 py-2 text-left font-medium text-ovation-muted">Name</th>
                <th className="px-4 py-2 text-left font-medium text-ovation-muted">Slug</th>
                <th className="px-4 py-2 text-left font-medium text-ovation-muted">Organisation</th>
                <th className="px-4 py-2 text-left font-medium text-ovation-muted">Shows</th>
                <th className="px-4 py-2 text-left font-medium text-ovation-muted">Members</th>
                <th className="px-4 py-2 text-left font-medium text-ovation-muted">Created</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-ovation-ink/5">
              {artists.map((a) => (
                <tr key={a.id} className="hover:bg-ovation-ink/5">
                  <td className="px-4 py-2 font-medium text-ovation-ink">{a.name}</td>
                  <td className="px-4 py-2 text-ovation-muted">{a.slug}</td>
                  <td className="px-4 py-2 text-ovation-ink">
                    {a.organisation?.name ?? '—'}
                  </td>
                  <td className="px-4 py-2 text-ovation-ink">{a.showCount}</td>
                  <td className="px-4 py-2 text-ovation-ink">{a.memberCount}</td>
                  <td className="px-4 py-2 text-ovation-muted">
                    {new Date(a.createdAt).toLocaleDateString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="flex items-center justify-between">
        <button
          type="button"
          disabled={page === 1}
          onClick={() => setPage((p) => p - 1)}
          className="rounded-md border border-ovation-ink/20 px-3 py-1 text-sm disabled:opacity-50"
        >
          Previous
        </button>
        <span className="text-sm text-ovation-muted">Page {page}</span>
        <button
          type="button"
          disabled={artists.length < limit}
          onClick={() => setPage((p) => p + 1)}
          className="rounded-md border border-ovation-ink/20 px-3 py-1 text-sm disabled:opacity-50"
        >
          Next
        </button>
      </div>
    </div>
  )
}

// ── Organisations Tab ──────────────────────────────────────────────────────────

function OrganisationsTab() {
  const [orgs, setOrgs] = useState<AdminOrg[]>([])
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [searchInput, setSearchInput] = useState('')
  const [loading, setLoading] = useState(true)

  const limit = 25

  useEffect(() => {
    async function load() {
      setLoading(true)
      try {
        const params = new URLSearchParams({ page: String(page), limit: String(limit) })
        if (search) params.set('search', search)
        const res = await apiFetch<AdminOrg[]>(`/admin/organisations?${params}`)
        setOrgs(res)
      } catch (err) {
        console.error('Failed to load organisations', err)
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [page, search])

  function handleSearch(e: React.FormEvent) {
    e.preventDefault()
    setPage(1)
    setSearch(searchInput)
  }

  return (
    <div className="space-y-4">
      <form onSubmit={handleSearch} className="flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ovation-muted" />
          <input
            type="text"
            placeholder="Search organisations..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="w-full rounded-md border border-ovation-ink/20 bg-white py-2 pl-10 pr-4 text-sm focus:border-ovation-ink focus:outline-none"
          />
        </div>
        <button
          type="submit"
          className="rounded-md bg-ovation-ink px-4 py-2 text-sm text-ovation-paper hover:bg-ovation-ink/90"
        >
          Search
        </button>
      </form>

      {loading ? (
        <LoadingSpinner />
      ) : (
        <div className="overflow-hidden rounded-lg border border-ovation-ink/10">
          <table className="w-full text-sm">
            <thead className="bg-ovation-ink/5">
              <tr>
                <th className="px-4 py-2 text-left font-medium text-ovation-muted">Name</th>
                <th className="px-4 py-2 text-left font-medium text-ovation-muted">Slug</th>
                <th className="px-4 py-2 text-left font-medium text-ovation-muted">Plan</th>
                <th className="px-4 py-2 text-left font-medium text-ovation-muted">Artists</th>
                <th className="px-4 py-2 text-left font-medium text-ovation-muted">Users</th>
                <th className="px-4 py-2 text-left font-medium text-ovation-muted">Created</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-ovation-ink/5">
              {orgs.map((o) => (
                <tr key={o.id} className="hover:bg-ovation-ink/5">
                  <td className="px-4 py-2 font-medium text-ovation-ink">{o.name}</td>
                  <td className="px-4 py-2 text-ovation-muted">{o.slug}</td>
                  <td className="px-4 py-2">
                    <span className="rounded bg-ovation-ink/10 px-2 py-0.5 text-xs">
                      {o.plan ?? 'free'}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-ovation-ink">{o.artistCount}</td>
                  <td className="px-4 py-2 text-ovation-ink">{o.userCount}</td>
                  <td className="px-4 py-2 text-ovation-muted">
                    {new Date(o.createdAt).toLocaleDateString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="flex items-center justify-between">
        <button
          type="button"
          disabled={page === 1}
          onClick={() => setPage((p) => p - 1)}
          className="rounded-md border border-ovation-ink/20 px-3 py-1 text-sm disabled:opacity-50"
        >
          Previous
        </button>
        <span className="text-sm text-ovation-muted">Page {page}</span>
        <button
          type="button"
          disabled={orgs.length < limit}
          onClick={() => setPage((p) => p + 1)}
          className="rounded-md border border-ovation-ink/20 px-3 py-1 text-sm disabled:opacity-50"
        >
          Next
        </button>
      </div>
    </div>
  )
}

// ── Shared Components ──────────────────────────────────────────────────────────

function StatCard({
  label,
  value,
  icon: Icon,
}: {
  label: string
  value: number
  icon: typeof Shield
}) {
  return (
    <div className="rounded-lg border border-ovation-ink/10 bg-white p-4">
      <div className="flex items-center justify-between">
        <span className="text-sm text-ovation-muted">{label}</span>
        <Icon className="h-5 w-5 text-ovation-muted" />
      </div>
      <div className="mt-2 text-3xl font-bold text-ovation-ink">
        {value.toLocaleString()}
      </div>
    </div>
  )
}

function AccountTypeBadge({ type }: { type: string }) {
  const colors: Record<string, string> = {
    internal: 'bg-purple-100 text-purple-800',
    artist: 'bg-blue-100 text-blue-800',
    crew_pro: 'bg-green-100 text-green-800',
    free: 'bg-gray-100 text-gray-800',
  }
  return (
    <span className={`rounded px-2 py-0.5 text-xs font-medium ${colors[type] ?? colors.free}`}>
      {type}
    </span>
  )
}

function LoadingSpinner() {
  return (
    <div className="flex items-center justify-center py-12">
      <div className="h-6 w-6 animate-spin rounded-full border-2 border-ovation-ink/20 border-t-ovation-ink" />
    </div>
  )
}
